import { Box, Columns, Icon } from 'modules/af_cm_ui_kit';
import fields, { FIELDS, INPUTS } from 'modules/cm/fields/types';
import React, { useEffect } from 'react';
import { SubPageTemplate } from '../../partials';
import { PropertyOccupant, Props } from './Property.types';
import validationSchema from './validationSchema';
import styles from './Property.module.scss';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import { yesNoOptions } from 'utils/form/formUtils';
import useCaseIsReadOnly from 'helpers/hooks/useCaseIsReadOnly/useCaseIsReadOnly';
import usePepperError from 'modules/cm/pages/CaseDetailsPage/hooks/useFieldError';
import UpdatableField from 'modules/cm/fields/UpdatableField';
import { GetErrorsPathsFunction } from '../../hooks/useFieldError/useFieldError';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import useYupValidationResolver from '../../../../../../form/yup/reactHookFormResolver';
import { propertyApi } from '../../../../api';
import { updateProperty } from '../../../../store/cases/update/actions';
import { useDispatch } from 'react-redux';
import { saveCase, setErrorModal } from '../../../../store/cases/actions';
import useSaveButtonClicked from '../../hooks/useSaveButtonClicked';
import * as subPages from '../../../../store/subPages';
import { SUBPAGES } from '../../../../store/subPages/types';
import CheckDirty from '../../../../components/CheckDirty/CheckDirty';

type OccupantEditProps = {
  onDelete: () => void;
  index: number;
  isCaseReadOnly: boolean;
  modulePrefix: string;
  getErrors: GetErrorsPathsFunction;
};

const OccupantEdit = ({ modulePrefix, index, onDelete, isCaseReadOnly, getErrors }: OccupantEditProps) => {
  return (
    <>
      <div className={styles.occupantInputWithAction}>
        <UpdatableField
          fieldType={INPUTS.TEXT}
          modulePrefix={modulePrefix}
          field={fields[FIELDS.PROPERTY_OCCUPANTS_PREFIX](index)[FIELDS.PROPERTY_OCCUPANT_FULLNAME]}
          className={styles.inputWrapperWidth}
          getErrors={getErrors}
        />
        <div className={styles.actions}>
          <button className={styles.button} type="button" onClick={() => onDelete()} disabled={isCaseReadOnly}>
            <Icon type="trash" />
          </button>
        </div>
      </div>
      <UpdatableField
        fieldType={INPUTS.TEXT}
        modulePrefix={modulePrefix}
        field={fields[FIELDS.PROPERTY_OCCUPANTS_PREFIX](index)[FIELDS.PROPERTY_OCCUPANT_DATE_OF_BIRTH]}
        variant={VARIANTS_TYPES.DATE}
        getErrors={getErrors}
      />

      <hr className={styles.divider} />
    </>
  );
};

const Property: React.FC<Props> = ({ modulePrefix, resources, data, initialData, caseId }) => {
  const isCaseReadOnly = useCaseIsReadOnly();
  const dispatch = useDispatch();
  const { getErrors } = usePepperError('property');
  const { addListener, removeListener } = useSaveButtonClicked();

  const methods = useForm<Props['data']>({
    mode: 'onBlur',
    defaultValues: initialData,
    resolver: useYupValidationResolver(validationSchema),
  });

  const { reset, control } = methods;

  const { fields: occupants, append, remove } = useFieldArray({
    control,
    keyName: 'key',
    name: FIELDS.PROPERTY_OCCUPANTS_PREFIX,
  });

  useEffect(() => {
    reset(data, {
      keepDefaultValues: true,
    });
  }, [data, reset]);

  useEffect(() => {
    const listener = addListener(() => {
      dispatch(updateProperty(methods.getValues()));
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
      dispatch(updateProperty(methods.getValues()));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOccupantDelete = (occupant: PropertyOccupant, index: number) => {
    propertyApi
      .deletePropertyOccupant(caseId, occupant.id)
      .then(() => {
        remove(index);
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };

  const addPropertyOccupant = () => {
    propertyApi
      .postPropertyOccupant(caseId)
      .then((res) => {
        append(res.data);
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };

  return (
    <SubPageTemplate title="Property">
      <FormProvider<Props['data']> {...methods}>
        <CheckDirty
          initialData={methods.getValues()}
          onDirtyChange={(isDirty) => {
            dispatch(subPages.actions.updateSubPageIsDirty(SUBPAGES.PROPERTY, isDirty));
          }}
        />
        <form onSubmit={() => {}}>
          <Columns spacing={4}>
            <div>
              <Box title="Address">
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_FLAT_NUMBER]}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_BUILDING_NAME]}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_BUILDING_NUMBER]}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_STREET]}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_CITY]}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_POSTCODE]}
                  variant={VARIANTS_TYPES.POSTCODE}
                />
              </Box>
              <Box title="Valuation">
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_INSTRUCTION_DATE]}
                  variant={VARIANTS_TYPES.DATE}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_INSPECTION_DATE]}
                  variant={VARIANTS_TYPES.DATE}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.PROPERTY_VALUATION_TYPE]}
                  options={resources.valuationTypes}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.PROPERTY_HAS_SPECIALIST_REPORT]}
                  options={yesNoOptions}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_TITLE_NUMBER]}
                />

                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_ESTIMATED_PROPERTY_VALUE]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_PROPERTY_PURCHASE_PRICE]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_ACTUAL_PROPERTY_VALUE]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
              </Box>
              <Box title="Other occupants">
                {occupants.map((field, index: number) => (
                  <OccupantEdit
                    key={`OccupantEdit.${index}`}
                    modulePrefix={modulePrefix}
                    onDelete={() => handleOccupantDelete((field as any) as PropertyOccupant, index)}
                    index={index}
                    isCaseReadOnly={isCaseReadOnly}
                    getErrors={getErrors}
                  />
                ))}

                <button
                  className={styles.button}
                  type="button"
                  onClick={addPropertyOccupant}
                  style={{ marginTop: 12, marginBottom: -16 }}
                  disabled={isCaseReadOnly}>
                  Add another occupant
                </button>
              </Box>
            </div>
            <div>
              <Box title="Property details">
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  options={yesNoOptions}
                  field={fields[FIELDS.PROPERTY_IS_PRIMARY_HOME]}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.PROPERTY_TYPE]}
                  options={resources.propertyTypes}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_FLOOR_NUMBER]}
                  variant={VARIANTS_TYPES.NUMBER}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_NUMBER_OF_FLOORS_IN_THE_BUILDING]}
                  variant={VARIANTS_TYPES.NUMBER}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  options={yesNoOptions}
                  field={fields[FIELDS.PROPERTY_HAS_LIFT_ACCESS]}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.PROPERTY_TENURE_TYPE]}
                  options={resources.tenureTypes}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_REMAINING_YEARS]}
                  variant={VARIANTS_TYPES.NUMBER}
                />

                <br />
                <label className={styles.bold}>Additional attributes</label>
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.CHECKBOX}
                  variant="inline"
                  field={fields[FIELDS.PROPERTY_IS_EX_LOCAL_AUTHORITY]}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.CHECKBOX}
                  variant="inline"
                  field={fields[FIELDS.PROPERTY_IS_RETIREMENT_HOME]}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.CHECKBOX}
                  variant="inline"
                  field={fields[FIELDS.PROPERTY_IS_RESTRICTED_COVENANT]}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.CHECKBOX}
                  variant="inline"
                  field={fields[FIELDS.PROPERTY_IS_ABOVE_COMMERCIAL_PREMISES]}
                />
                <br />

                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_COMMERCIAL_PREMISES_DETAILS]}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  options={yesNoOptions}
                  field={fields[FIELDS.PROPERTY_HAS_COMMERCIAL_USAGE_ABOVE25_PERCENT]}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_COMMERCIAL_USAGE_DETAILS]}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_NUMBER_OF_KITCHENS]}
                  variant={VARIANTS_TYPES.NUMBER}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_NUMBER_OF_BEDROOMS]}
                  variant={VARIANTS_TYPES.NUMBER}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.CHECKBOX}
                  variant="inline"
                  field={fields[FIELDS.PROPERTY_HAS_AGRICULTURAL_RESTRICTIONS]}
                />
                <br />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_PROPERTY_YEAR_BUILT]}
                  variant={VARIANTS_TYPES.NUMBER}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_WARRANTY_PROVIDER]}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.PROPERTY_IS_PROPERTY_NEW_BUILD]}
                  options={[
                    {
                      option: 'Yes',
                      value: 'true',
                    },
                    {
                      option: 'No',
                      value: 'false',
                    },
                  ]}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.PROPERTY_PROPERTY_CONSTRUCTION]}
                  options={resources.propertyConstructions}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_PROPERTY_DETAILS]}
                />
              </Box>
            </div>
          </Columns>
        </form>
      </FormProvider>
    </SubPageTemplate>
  );
};

export default Property;
